import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Cell } from "fixed-data-table-2"
import SegueLink from "app/static/frontend/middleware/SegueLink"
import { Link } from "react-router-dom"
import classNames from "classnames"
import Icon from "components/Icon"
import ImmutablePropTypes from "react-immutable-proptypes"
import swal from "sweetalert"
import { swalContents } from "sheets/components/helpTexts"
import { Icon as DSIcon } from "quorumdesign"
import { datumCellConnection } from "sheets/components/cells/cell-selectors/datum-cell-selectors"
import { connect } from "react-redux"
import Tooltip from "app/static/frontend/components/Tooltip"
import sheetConstants from "sheets/constants"

// components
import BulkUploadCell from "sheets/components/cells/BulkUploadCell"
import ParentChildCell from "sheets/components/cells/ParentChildCell"
import BulkSMSDatumCell from "sheets/components/cells/BulkSMSDatumCell"
import GrassrootsActionDatumCell from "app/static/frontend/sheets/components/cells/GrassrootsActionDatumCell"
import PotentialDuplicateDatumCell from "sheets/components/cells/PotentialDuplicateDatumCell"
import TransactionDatumCell from "sheets/components/cells/TransactionDatumCell"
import DashboardDatumCell from "sheets/components/cells/DashboardDatumCell"
import CommitteeDatumCell from "sheets/components/cells/CommitteeDatumCell"
import PressDatumCell from "sheets/components/cells/PressDatumCell"
import { IconWithTooltip } from "@/components/IconWithTooltip"

// helper functions
import { calculateTextWidth } from "imports/styleHelperFunctions"
import { generateStaticUrl, cleanUpHtml } from "shared/imports/sharedHelperFunctions"
import { BLANK_AVATAR_PATH, BrandColors } from "shared/imports/sharedConstants"
import { shouldHighlightDatumCell, getPartyKey } from "sheets/helper-functions"

// helper component to render tooltip as needed
export const ToolTipCell = ({ text, cellWidth, cell }) => {
    const textWidth = calculateTextWidth(sheetConstants.columnNameFont, text)
    return textWidth > cellWidth ? (
        <Tooltip delayShow={sheetConstants.CELL_TOOLTIP_DELAY} label={text} placement="right">
            {cell}
        </Tooltip>
    ) : (
        cell
    )
}

// render a datum cell for a person
// if the sheet is mobile, show only the first and last name of the person
export const PersonDatumCell = ({ datum, ...props }) => {
    const text = cleanUpHtml(datum.get("name"))
    const shouldShowImage = !props.isResourceTable
    const isCustom = datum.get("organization") ? true : false
    const imageUrl = isCustom ? datum.get("image_url") : generateStaticUrl(datum.get("image_url"))

    return (
        <ToolTipCell
            text={text}
            cellWidth={props.width}
            cell={
                <Cell {...props}>
                    {shouldShowImage && (
                        <img
                            alt=""
                            className={classNames("sheet-row-image", {
                                [getPartyKey(datum)]: true,
                            })}
                            src={datum.get("image_url") ? imageUrl : generateStaticUrl(BLANK_AVATAR_PATH)}
                        />
                    )}
                    <span>{text}</span>
                    {isCustom && (
                        <IconWithTooltip
                            icon="building"
                            iconColor={BrandColors.BRASS}
                            iconStyle={{ marginLeft: "3px" }}
                            label="Added by your organization"
                            tooltipPosition="right"
                            withArrow
                        />
                    )}
                </Cell>
            }
        />
    )
}

export const DisplayKeyCell = ({ datum, dataType, ...props }) => (
    <Cell {...props}>{cleanUpHtml(datum.get(DjangIO.app.models.QuorumDataType.by_value(dataType).display_key))}</Cell>
)

export const BillDatumCell = ({ datum, ...props }) => {
    const classes = classNames(
        props.className,
        "long-text",
        `large-text-cell-overflow-hidden large-text-cell-${props.rowHeightFactor || 1}`,
    )
    const regionEnum = DjangIO.app.models.Region.by_value(datum.get("region"))
    const text =
        props.isResourceTable && regionEnum ? `${regionEnum.region_name}: ${datum.get("title")}` : datum.get("title")
    const parsedText = cleanUpHtml(text)
    return (
        <ToolTipCell
            text={parsedText}
            cellWidth={props.width}
            cell={
                <Cell {...props} className={classes}>
                    {parsedText}
                </Cell>
            }
        />
    )
}

export const VoteDatumCell = ({ datum, ...props }) => {
    const text = cleanUpHtml(datum.get("question"))
    return <ToolTipCell text={text} cellWidth={props.width} cell={<Cell {...props}>{text}</Cell>} />
}

export const SupporterDatumCell = ({ datum, ...props }) => {
    const text = cleanUpHtml(datum.get("name") || "Unnamed Contact")
    return <ToolTipCell text={text} cellWidth={props.width} cell={<Cell {...props}>{text}</Cell>} />
}

export const AmendmentDatumCell = ({ datum, ...props }) => {
    const text = cleanUpHtml(datum.get("title"))
    return <ToolTipCell text={text} cellWidth={props.width} cell={<Cell {...props}>{text}</Cell>} />
}

export const PublicOrganizationDatumCell = ({ datum, ...props }) => {
    const text = cleanUpHtml(datum.get("name"))
    return <ToolTipCell text={text} cellWidth={props.width} cell={<Cell {...props}>{text}</Cell>} />
}

export const UserDatumCell = ({ datum, ...props }) => {
    const text = datum.get("user_info__full_name") || `${datum.get("first_name")} ${datum.get("last_name")}`
    return <ToolTipCell text={text} cellWidth={props.width} cell={<Cell {...props}>{text}</Cell>} />
}

export const BulkEmailDatumCell = ({ datum, ...props }) => {
    const extra = datum.get("_extra") && datum.get("_extra").toJS()

    const subject = datum.get("subject")
    const description = extra && extra.abtest_description
    const hasAttachments = extra && extra.has_attachments

    return (
        <Cell {...props}>
            {hasAttachments === "Yes" && (
                <span className="outbox-attachment-icon">
                    <DSIcon icon="paperclip" iconFamily="fal" />
                </span>
            )}
            {description || subject || "Untitled Draft"}
        </Cell>
    )
}

export const NoteDatumCell = ({ datum, ...props }) => (
    <Cell {...props}>
        {props.isResourceTable ? (
            <React.Fragment>
                <span>{cleanUpHtml(datum.get("humanized_text"))}</span>
            </React.Fragment>
        ) : (
            <React.Fragment>
                {datum.getIn(["cache_json", "icon"]) && (
                    <Icon name={datum.getIn(["cache_json", "icon"])} lg={true} className="sheet-row-icon" />
                )}
                <span>{datum.get("raw_content")}</span>
            </React.Fragment>
        )}
    </Cell>
)

export const CustomEventParticipantDatumCell = ({ datum, ...props }) => (
    <Cell {...props}>{datum.getIn(["_extra", "participant_name"])}</Cell>
)

export const PDFEmailDatumCell = ({ datum, ...props }) => (
    <Link
        onClick={() => {
            const downloadLink = datum.get("download_link")
            const link = downloadLink
                ? downloadLink.includes(".zip.zip")
                    ? downloadLink.replace(".zip.zip", ".zip")
                    : downloadLink
                : ""
            return datum.get("sent")
                ? swal(swalContents.downloadCenter.downloadSwal).then(
                      (isConfirm) => isConfirm && window.open(link, "_BLANK"),
                  )
                : swal(swalContents.downloadCenter.notReadyYetSwal).then(
                      (isConfirm) => !isConfirm && window.location.reload(),
                  )
        }}
    >
        <Cell {...props}>{datum.get("title")}</Cell>
    </Link>
)

export const DLCDownloadDatumCell = ({ datum, ...props }) => (
    <Link
        onClick={() => {
            const link = datum.get("url")
            return link
                ? // if there is a link, create a new tab that will segue to the download url in s3
                  // which will download the zip file and then close the tab.
                  swal(swalContents.downloadCenter.downloadSwal).then(
                      (isConfirm) => isConfirm && window.open(link, "_BLANK"),
                  )
                : swal(swalContents.downloadCenter.notReadyYetSwal).then(
                      (isConfirm) => !isConfirm && window.location.reload(),
                  )
        }}
    >
        <Cell {...props}>{datum.get("title")}</Cell>
    </Link>
)

// prevent "Amendment" being pushed to a second line on February reports
const NoOverflowSpan = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const PacFileDatumCell = ({ datum, ...props }) => {
    const isAmendment = !!datum.get("amends_filing_id")
    const tooltip = isAmendment ? `Amendment of ${datum.get("amends_filing_id")}` : undefined
    return (
        <Cell {...props} className={classNames(props.className, `pacfile parent-child`)} title={tooltip}>
            <NoOverflowSpan>{cleanUpHtml(datum.get("filing_display_key"))}</NoOverflowSpan>
        </Cell>
    )
}

const datumCellForDataType = (dataType, props) => {
    switch (DjangIO.app.models.QuorumDataType.by_value(dataType).key) {
        case DjangIO.app.models.QuorumDataType.person.key:
        case DjangIO.app.models.QuorumDataType.staffer.key:
            return <PersonDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.press_contact.key:
            return <PressDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.bill.key:
            return <BillDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.vote.key:
            return <VoteDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.supporter.key:
            return <SupporterDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.public_organization.key:
            return <PublicOrganizationDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.amendment.key:
            return <AmendmentDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.bulk_email.key:
            return <BulkEmailDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.bulk_sms.key:
            return <BulkSMSDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.custom_event_participant.key:
            return <CustomEventParticipantDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.pdf_email.key:
            return <PDFEmailDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.new_pdf_email.key:
            return <DLCDownloadDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.user.key:
            return <UserDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.bulk_upload.key:
            return <BulkUploadCell {...props} />
        case DjangIO.app.models.QuorumDataType.issue_management.key:
            return <ParentChildCell {...props} />
        case DjangIO.app.models.QuorumDataType.note.key:
            return <NoteDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.potential_duplicates.key:
            return <PotentialDuplicateDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.transaction.key:
            return <TransactionDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.dashboard.key:
            return <DashboardDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.committee.key:
            return <CommitteeDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.grassroots_action.key:
            return <GrassrootsActionDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.pac_filings.key:
            return <PacFileDatumCell {...props} />
        default:
            return <DisplayKeyCell dataType={dataType} {...props} />
    }
}

export const DatumCell = ({
    dataType,
    datum,
    isDesignSystemResourceTable,
    optionalPreSegueFunction,
    shouldRenderLink,
    datumCellClassNames,
    cellLink,
    ...props
}) => (
    <div>
        {/* If we are rendering a public sheet, we don't want to render links (also custom_data, state cause they dont have profiles) */}
        {shouldRenderLink ? (
            <div
                className={classNames({
                    "default-datum-cell": true,
                    "bold-text": isDesignSystemResourceTable && shouldHighlightDatumCell(datum, dataType),
                })}
            >
                <SegueLink
                    className={datumCellClassNames}
                    to={cellLink}
                    optionalPreSegueFunction={optionalPreSegueFunction}
                    dataType={dataType}
                >
                    {datumCellForDataType(dataType, { className: "datum-cell", datum, ...props })}
                </SegueLink>
            </div>
        ) : (
            datumCellForDataType(dataType, { className: "datum-cell", datum, ...props })
        )}
    </div>
)

DatumCell.propTypes = {
    isPublicSheet: PropTypes.bool,
    hasProjectManagementPermission: PropTypes.bool,
    datum: ImmutablePropTypes.map.isRequired,
    dataType: PropTypes.number.isRequired,
    datumUrlOverride: PropTypes.string,
}
DatumCell.defaultProps = {
    isPublicSheet: false,
    hasProjectManagementPermission: false,
    datumUrlOverride: "",
}
DisplayKeyCell.propTypes = {
    dataType: PropTypes.number.isRequired,
    datum: ImmutablePropTypes.map.isRequired,
}
BillDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
VoteDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
SupporterDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
AmendmentDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
PublicOrganizationDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
BulkEmailDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
PDFEmailDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
PersonDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}

export default connect((state, props) => datumCellConnection(state, props))(DatumCell)
